import { Divider, Button, Upload } from 'antd'
import { cloneDeep } from 'lodash'
import { nanoid } from 'nanoid'
import { RightCircleFilled } from '@ant-design/icons'

import { message as antMessage } from '@/App'
import { chatState, chatAction } from '@/store'
import { SessionStatuTypes, RoleTypes } from '@/constants'
import { uploadFile, updatePlugins } from '@/api/chat'
import { DocFileIcon } from '@/pages/chat/widget/Plugins/DocFile'
import pluginDocSvg from '@/assets/images/plugins/plugin_doc.svg?react'
import pluginpPptSvg from '@/assets/images/plugins/plugin_ppt.svg?react'
import pluginpFlowSvg from '@/assets/images/plugins/plugin_processon.svg?react'
import pluginpNewsSvg from '@/assets/images/plugins/plugin_news.svg?react'

import styles from './index.module.less'

interface IPluginConfig {
  name: string
  desc: string
  accept?: string
  value: string
  icon: any
  example?: any[]
  popContent: () => JSX.Element
  [key: string]: any
}

export const onSelectPlugins = async (pluginType: string, toggle = false) => {
  const { plugins } = chatState
  if (!plugins.has(pluginType)) {
    plugins.clear()
    if (pluginType === pluginConfigDoc.value) {
      plugins.set(pluginConfigDoc.value, [])
    } else {
      plugins.set(pluginType, true)
    }
    await updatePlugins({
      session: chatState.sessionId,
      list: Array.from(plugins.keys()),
    })
  } else {
    if (toggle) {
      plugins.delete(pluginType)
      await updatePlugins({
        session: chatState.sessionId,
        list: Array.from(plugins.keys()),
      })
    }
  }
}

// 发送文件消息到对话框
const onSubmitDocMessage = (input: any) => {
  if (chatState.sessionStatu === SessionStatuTypes.risk) {
    antMessage.open({
      key: 'chat',
      type: 'warning',
      content: '请新建会话再发送',
    })
    return
  }
  if (chatState.sessionStatu !== SessionStatuTypes.ready) {
    antMessage.open({
      key: 'chat',
      type: 'warning',
      content: '请等待回复后再发送',
    })
    return
  }
  if (!chatState.sessionId) {
    chatState.sessionId = nanoid()
  }
  const sendMessage = {
    ...input,
    question_id: nanoid(),
    role: RoleTypes.user,
    reAnswer: 0,
  }
  const messages = cloneDeep(chatState.sessionMessages)
  messages.push(sendMessage, {
    uuid: nanoid(),
    role: RoleTypes.assistant,
    loading: true,
    nostop: true,
    out_finished: false,
  })
  chatAction.updateMessages(messages)
  chatAction.updateSessionStatu(SessionStatuTypes.pending)
}

// 收到文档解析结果
const onUploadParsed = (data: any, file: any) => {
  const { plugins } = chatState

  const messages = cloneDeep(chatState.sessionMessages)
  let lastMessage = {
    ...(messages[messages.length - 1] || {}),
    loading: false,
    pluginInfo: {
      ...data.plugins,
      pluginName: '文档解析',
      value: pluginConfigDoc.value,
      status: data.plugins?.status ?? 0,
    },
    related: data.plugins?.status === 1 ? file.related : null,
  }
  if (data.plugins?.status === 1) {
    // 添加文档到文件列表
    plugins.set(pluginConfigDoc.value, [
      ...plugins.get(pluginConfigDoc.value),
      {
        name: file.name,
        size: file.size,
        url: data?.file_path,
      },
    ])
  }
  messages.splice(messages.length - 1, 1, lastMessage)
  chatAction.updateMessages(messages)
  chatAction.updateSessionStatu(SessionStatuTypes.ready)
}

const canUploadFile = async (fileSize: any, fileExtension: string) => {
  if (chatState.sessionStatu !== SessionStatuTypes.ready) {
    antMessage.open({
      key: 'chat',
      type: 'warning',
      content: '请等待当前文档解析完成后再上传',
    })
    return
  }

  const { plugins } = chatState
  const docs = plugins.get(pluginConfigDoc.value)
  await onSelectPlugins(pluginConfigDoc.value)

  const totalSize = docs?.reduce((total: number, item: any) => {
    total = total + (item.size as number)
  }, fileSize)
  if (!pluginConfigDoc.accept.includes(fileExtension)) {
    antMessage.open({
      key: 'chat',
      type: 'error',
      content: `请上传支持的文档类型：${pluginConfigDoc.accept}`,
    })
    return false
  }
  if (totalSize / 1024 / 1024 > 10) {
    antMessage.open({
      key: 'chat',
      type: 'error',
      content: '上传文档总大小不能超过10MB',
    })
    return false
  }
  if (docs?.length >= 3) {
    antMessage.open({
      key: 'chat',
      type: 'error',
      content: '上传文档数量不能超出三个',
    })
    return false
  }
  return true
}

const onSendExampleText = async (pluginType: string, text: string) => {
  await onSelectPlugins(pluginType)
  chatAction.setInputText(text)
}

export const onSendExampleDocs = async (docItem: any) => {
  if (!(await canUploadFile(docItem.size, docItem.extension))) {
    return false
  }
  onSubmitDocMessage({
    file: {
      plugin: 'docs',
      name: docItem.name,
    },
  })
  try {
    const { data } = await uploadFile({ url: docItem.url })
    onUploadParsed(data, {
      name: docItem.name,
      size: docItem.size,
      related: docItem?.related || null,
    })
  } catch (error) {
    onUploadParsed(
      {
        status: 0,
      },
      {
        name: docItem.name,
        size: docItem.size,
      },
    )
  }
}

export const beforeUploadDocs = async (file: any, exampleFile?: any) => {
  const fileExtension = file.name?.split('.').pop() || ''

  if (!(await canUploadFile(file.size, fileExtension))) {
    return false
  }

  onSubmitDocMessage({
    file: {
      plugin: 'docs',
      name: file.name,
    },
  })
  try {
    const { data } = await uploadFile({ file })
    onUploadParsed(data, {
      name: file.name,
      size: file.size,
      related: exampleFile?.related || null,
    })
  } catch (error) {
    onUploadParsed(
      {
        status: 0,
      },
      {
        name: file.name,
        size: file.size,
      },
    )
  }
  return false
}

export const pluginConfigDoc = {
  name: '文档问答',
  desc: '可基于文档完成摘要、问答、创作等任务，仅支持 10MB 以内文档，暂时只支持中文内容。',
  value: 'docs',
  accept: '.docx,.pdf,.txt,.md',
  icon: pluginDocSvg,
  example: [
    {
      name: '公路渡口管理规定.txt',
      url: 'https://zdtc-cdn.wair.ac.cn/knowledge/%E5%85%AC%E8%B7%AF%E6%B8%A1%E5%8F%A3%E7%AE%A1%E7%90%86%E8%A7%84%E5%AE%9A.txt',
      size: 53246,
      extension: '.txt',
      related: [
        '《公路渡口管理规定》颁布的时间是什么时候？',
        '公路渡口是由谁负责修建、养护和管理的？',
        '公路渡口的安全管理工作应接受哪个部门的监督和指导？',
      ],
    },
    {
      name: '中国人民共和国宪法.txt',
      url: 'https://zdtc-cdn.wair.ac.cn/knowledge/%E4%B8%AD%E5%9B%BD%E4%BA%BA%E6%B0%91%E5%85%B1%E5%92%8C%E5%9B%BD%E5%AE%AA%E6%B3%95.txt',
      size: 8489,
      extension: '.txt',
      related: [
        '中国的宪法是什么时候颁布的？',
        '中华人民共和国的根本制度是什么？',
        '中国的最高国家权力机关是什么？',
      ],
    },
  ],
  popContent: () => {
    return (
      <div className={styles.detailPopContent}>
        <div className={styles.title}>{pluginConfigDoc.name}</div>
        <div className={styles.desc}>{pluginConfigDoc.desc}</div>
        <Upload
          accept={pluginConfigDoc.accept}
          beforeUpload={beforeUploadDocs}
          maxCount={1}
          showUploadList={false}
        >
          <Button type="primary" size="small" style={{ fontSize: '12px' }}>
            上传文档
          </Button>
        </Upload>
        <Divider style={{ margin: '16px 0' }} />
        <div className={styles.example}>
          <div className={styles.exampleLabel}>示例文件</div>
          <div className={styles.fileWrap}>
            {pluginConfigDoc.example.map(item => (
              <div
                key={item.url}
                className={styles.file}
                onClick={() => {
                  onSendExampleDocs(item)
                }}
              >
                <div className={styles.fileIcon}>
                  <DocFileIcon fileName={item.url} />
                </div>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
}

export const pluginConfigPpt = {
  name: 'PPT生成',
  desc: '可通过你简单的一句话，生成符合你要求的完整PPT。',
  value: 'ppt',
  icon: pluginpPptSvg,
  example: [
    {
      text: '请帮我生成一个年终总结报告PPT',
    },
    {
      text: '请帮我制作一个毕业答辩的PPT',
    },
  ],
  popContent: () => {
    return (
      <div className={styles.detailPopContent}>
        <div className={styles.title}>{pluginConfigPpt.name}</div>
        <div className={styles.desc}>{pluginConfigPpt.desc}</div>
        <Button
          type="primary"
          size="small"
          style={{ fontSize: '12px' }}
          onClick={() => {
            onSelectPlugins(pluginConfigPpt.value)
          }}
        >
          立即使用
        </Button>
        <Divider className="my-[16px]" />
        <div className={styles.example}>
          <div className={styles.exampleLabel}>示例</div>
          <div className={styles.exampleContent}>
            {pluginConfigPpt.example.map(item => (
              <div
                key={item.text}
                className={styles.exampleItem}
                onClick={() => {
                  onSendExampleText(pluginConfigPpt.value, item.text)
                }}
              >
                <span>{item.text}</span>
                <RightCircleFilled />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
}

export const pluginConfigProcesson = {
  name: '特殊图生成',
  desc: '可通过你提供的相关信息，一键生成可编辑流程图/思维导图。',
  value: 'processon',
  icon: pluginpFlowSvg,
  example: [
    {
      text: '请帮我生成一个财务报销的流程图 ',
    },
  ],
  popContent: () => {
    return (
      <div className={styles.detailPopContent}>
        <div className={styles.title}>{pluginConfigProcesson.name}</div>
        <div className={styles.desc}>{pluginConfigProcesson.desc}</div>
        <Button
          type="primary"
          size="small"
          style={{ fontSize: '12px' }}
          onClick={() => {
            onSelectPlugins(pluginConfigProcesson.value)
          }}
        >
          立即使用
        </Button>
        <Divider className="my-[16px]" />
        <div className={styles.example}>
          <div className={styles.exampleLabel}>示例</div>
          <div className={styles.exampleContent}>
            {pluginConfigProcesson.example.map(item => (
              <div
                key={item.text}
                className={styles.exampleItem}
                onClick={() => {
                  onSendExampleText(pluginConfigProcesson.value, item.text)
                }}
              >
                <span className="pr-[18px]">{item.text}</span>
                <RightCircleFilled />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
}

export const pluginConfigNews = {
  name: '实时新闻搜索',
  desc: '为保证获取更实时准确的信息，紫东太初默认开启实时新闻搜索插件，暂不支持关闭。',
  value: 'news',
  icon: pluginpNewsSvg,
  readonly: true,
  popContent: () => {
    return (
      <div className={styles.detailPopContent}>
        <div className={styles.title}>{pluginConfigNews.name}</div>
        <div className="text-[#595959]">{pluginConfigNews.desc}</div>
      </div>
    )
  },
}

export const PluginOptions: IPluginConfig[] = [
  // pluginConfigDoc,
  pluginConfigPpt,
  pluginConfigProcesson,
  pluginConfigNews,
]
